import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { SESClient, SendEmailCommand } from '@aws-sdk/client-ses';

interface Applicant {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  unitNumber?: string;
}

interface FormData {
  location: string;
  mainApplicant: Applicant;
  hasOtherApplicants: boolean;
  otherApplicants: Applicant[];
}

const ContactForm: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  // Location-based email configuration
  const locationEmailConfig = {
    'buckman-terrace': {
      name: 'Buckman Terrace',
      emails: ['BuckmanManager@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Buckman Terrace'
    },
    'belmar-commons': {
      name: 'Belmar Commons',
      emails: ['BelmarManager@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Belmar Commons'
    },
    'capitola-commons': {
      name: 'Capitola Commons',
      emails: ['CapitolaManager@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Capitola Commons'
    },
    'cathedral-park': {
      name: 'Cathedral Park',
      emails: ['CathedralManager@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Cathedral Park'
    },
    'chesapeake-pointe': {
      name: 'Chesapeake Pointe',
      emails: ['ChesapeakeManager@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Chesapeake Pointe'
    },
    'cornerstone': {
      name: 'Cornerstone',
      emails: ['CornerstoneManager@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Cornerstone'
    },
    'gresham-central': {
      name: 'Gresham Central',
      emails: ['GreshamManager@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Gresham Central'
    },
    'hawthorne-44-apartments': {
      name: 'Hawthorne 44 Apartments',
      emails: ['Hawthorne44@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Hawthorne 44 Apartments'
    },
    'hillcrest': {
      name: 'Hillcrest',
      emails: ['JamesL@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Hillcrest'
    },
    'hilltop-house': {
      name: 'Hilltop House',
      emails: ['JamesL@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Hilltop House'
    },
    'king-city': {
      name: 'King City',
      emails: ['kingcitymanager@sremi.com','kingcityassistant@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for King City'
    },
    'oakridge': {
      name: 'Oakridge',
      emails: ['oakridgemanager@sremi.com','oakridgeassistant@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Oakridge'
    },
    'overlook-pointe': {
      name: 'Overlook Pointe',
      emails: ['OverlookManager@sremi.com','OverlookAssistant@sremi.com','overlookassistant2@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Overlook Pointe'
    },
    'portland-terrace': {
      name: 'Portland Terrace',
      emails: ['JamesL@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Portland Terrace'
    },
    'riverwood': {
      name: 'Riverwood',
      emails: ['RiverwoodManager@sremi.com','RiverwoodAssistant@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Riverwood'
    },
    'sophias-view': {
      name: 'Sophia\'s View',
      emails: ['JamesL@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Sophia\'s View'
    },
    'springbrook': {
      name: 'Springbrook',
      emails: ['springbrook@srmei.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Springbrook'
    },
    'swale-creek': {
      name: 'Swale Creek',
      emails: ['OakridgeManager@sremi.com','OakridgeAssistant@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Swale Creek'
    },
    'vista-avenue': {
      name: 'Vista Avenue',
      emails: ['VistaAvenue@srmei.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Vista Avenue'
    },
    'woodlake': {
      name: 'Woodlake',
      emails: ['WoodlakeManager@sremi.com','BethW@sremi.com'],
      ccEmails: ['EsmeraldaR@sremi.com'], // Add CC emails as needed
      bccEmails: ['service@rental-screening.com'], // Add BCC emails as needed
      subject: 'Application Request for Woodlake'
    }
  };

  const { register, control, handleSubmit, watch, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      location: '',
      mainApplicant: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        unitNumber: ''
      },
      hasOtherApplicants: false,
      otherApplicants: []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "otherApplicants"
  });

  const hasOtherApplicants = watch("hasOtherApplicants");

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      const sesClient = new SESClient({
        region: process.env.REACT_APP_AWS_REGION,
        credentials: {
          accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID!,
          secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY!
        }
      });

      const emailContent = `
        Location: ${locationEmailConfig[data.location as keyof typeof locationEmailConfig]?.name || 'Not specified'}
        Primary Applicant:
        Requested Unit Number: ${data.mainApplicant.unitNumber || 'Not provided'}
        Name: ${data.mainApplicant.firstName} ${data.mainApplicant.lastName}
        Email: ${data.mainApplicant.email}
        Phone: ${data.mainApplicant.phone || 'Not provided'}
        
        ${data.hasOtherApplicants ? `Additional Applicants:
        ${data.otherApplicants.map((applicant, index) => `
        Applicant ${index + 1}:
        Name: ${applicant.firstName} ${applicant.lastName}
        Email: ${applicant.email}
        `).join('\n')}` : ''}
      `;

      // Get location-specific email configuration
      const selectedLocation = locationEmailConfig[data.location as keyof typeof locationEmailConfig];
      
      if (!selectedLocation) {
        throw new Error('Please select a valid location');
      }

      const toAddrs = selectedLocation.emails;
      const ccAddrs = selectedLocation.ccEmails || [];
      const bccAddrs = selectedLocation.bccEmails || [];

      if (toAddrs.length === 0) {
        throw new Error('No recipient addresses configured for selected location');
      }

      const command = new SendEmailCommand({
        Source: process.env.REACT_APP_AWS_SES_FROM_EMAIL,
        Destination: {
          ToAddresses: toAddrs,
          ...(ccAddrs.length > 0 && { CcAddresses: ccAddrs }),
          ...(bccAddrs.length > 0 && { BccAddresses: bccAddrs })
        },
        Message: {
          Subject: { Data: selectedLocation.subject },
          Body: { Text: { Data: emailContent } }
        }
      });

      await sesClient.send(command);
  setNotification({ 
    type: 'success', 
    message: `Form submitted successfully!<br/>Your application for ${selectedLocation.name} has been sent.<br/>The property manager will reach out to you shortly.` 
  });
  // Reset the form to default values
  reset();
  // clear notification after 10 seconds
  setTimeout(() => setNotification(null), 10000);
    } catch (error) {
      console.error('Error sending email:', error);
      setNotification({ type: 'error', message: 'Error submitting form. Please try again.' });
      setTimeout(() => setNotification(null), 10000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="contact-form">
      {notification && (
        <div className={`notification ${notification.type}`} role="status">
          <div dangerouslySetInnerHTML={{ __html: notification.message }} />
        </div>
      )}
      
      <div className="location-selection">
        <h2>Please select the property you wish to apply to:</h2>
        <div className="form-group">
          <select
            {...register("location", { required: "Please select a location" })}
            defaultValue=""
          >
            <option value="" disabled>Select Property</option>
            {Object.entries(locationEmailConfig)
              .sort(([, a], [, b]) => a.name.localeCompare(b.name))
              .map(([key, config]) => (
                <option key={key} value={key}>
                  {config.name}
                </option>
              ))}
          </select>
          {errors.location && <span className="error">{errors.location.message}</span>}
        </div>
      </div>

      <div className="main-applicant">
        <h2>Primary Applicant</h2>
        <div className="form-group">
          <input
            {...register("mainApplicant.unitNumber")}
            placeholder="Requested Unit Number (optional)"
            type="text"
          />
          {errors.mainApplicant?.unitNumber && <span className="error">{errors.mainApplicant.unitNumber.message}</span>}
        </div>
        <div className="form-group">
          <input
            {...register("mainApplicant.firstName", { required: "First name is required" })}
            placeholder="First Name"
          />
          {errors.mainApplicant?.firstName && <span className="error">{errors.mainApplicant.firstName.message}</span>}
        </div>

        <div className="form-group">
          <input
            {...register("mainApplicant.lastName", { required: "Last name is required" })}
            placeholder="Last Name"
          />
          {errors.mainApplicant?.lastName && <span className="error">{errors.mainApplicant.lastName.message}</span>}
        </div>

        <div className="form-group">
          <input
            {...register("mainApplicant.email", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address"
              }
            })}
            placeholder="Email"
            type="email"
          />
          {errors.mainApplicant?.email && <span className="error">{errors.mainApplicant.email.message}</span>}
        </div>

        <div className="form-group">
          <input
            {...register("mainApplicant.phone", {
              required: "Phone number is required",
              pattern: {
                value: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
                message: "Please enter a valid 10-digit phone number (e.g., 555-123-4567)"
              },
              minLength: {
                value: 10,
                message: "Phone number must be at least 10 digits"
              }
            })}
            placeholder="Phone Number (10 digits)"
            type="tel"
            aria-required="true"
          />
          {errors.mainApplicant?.phone && <span className="error">{errors.mainApplicant.phone.message}</span>}
        </div>
      </div>

      <div className="other-applicants">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="hasOtherApplicants"
              {...register("hasOtherApplicants")}
            />
            <label htmlFor="hasOtherApplicants">Check here if there are other applicants older than 18 years of age.</label>
          </div>

        {hasOtherApplicants && (
          <div className="additional-applicants">
            {fields.map((field, index) => (
              <div key={field.id} className="applicant">
                <h3>Additional Applicant {index + 1}</h3>
                <div className="form-group">
                  <input
                    {...register(`otherApplicants.${index}.firstName` as const, { required: "First name is required" })}
                    placeholder="First Name"
                  />
                </div>

                <div className="form-group">
                  <input
                    {...register(`otherApplicants.${index}.lastName` as const, { required: "Last name is required" })}
                    placeholder="Last Name"
                  />
                </div>

                <div className="form-group">
                  <input
                    {...register(`otherApplicants.${index}.email` as const, {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address"
                      }
                    })}
                    placeholder="Email"
                    type="email"
                  />
                </div>

                <button type="button" onClick={() => remove(index)}>Remove Applicant</button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => append({ firstName: '', lastName: '', email: '' })}
            >
              Add Another Applicant
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>

        <button
          type="button"
          onClick={() => { reset(); setNotification(null); }}
          disabled={isSubmitting}
        >
          Clear
        </button>
      </div>
    </form>
  );
};

export default ContactForm;